import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { 
  Car, 
  LayoutDashboard, 
  FileText, 
  AlertTriangle, 
  History,
  LogOut,
  Store,
  Gift,
  Plus,
  Pencil,
  Loader2,
  Users,
  Building,
  MapPin,
  Coins,
  Target,
  Calendar,
  BarChart3,
  Wallet,
  DollarSign,
  Package
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { ParkWiseLogo } from "../../components/ui/parkwise-logo";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../../components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { toast } from "sonner";
import axios from "axios";

const API = '/api';

export default function AdminRewards() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [rules, setRules] = useState([]);
  const [sites, setSites] = useState([]);
  const [ledger, setLedger] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rulesLoading, setRulesLoading] = useState(false);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("rules");
  
  // Filters
  const [siteFilter, setSiteFilter] = useState("all");
  const [vehicleFilter, setVehicleFilter] = useState("");
  const [debouncedVehicleFilter, setDebouncedVehicleFilter] = useState("");
  const [showActiveRulesOnly, setShowActiveRulesOnly] = useState(false);
  
  // Dialog states
  const [showRuleDialog, setShowRuleDialog] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  
  // Form state
  const [ruleForm, setRuleForm] = useState({
    site_id: "",
    name: "",
    rule_type: "per_visit",
    credit_value: "",
    visits_required: "",
    time_window_days: ""
  });

  // Debounce vehicle filter
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedVehicleFilter(vehicleFilter);
    }, 500);
    return () => clearTimeout(timer);
  }, [vehicleFilter]);

  useEffect(() => {
    checkAuth();
  }, []);

  // Initial data load
  useEffect(() => {
    if (user) {
      fetchInitialData();
    }
  }, [user]);

  // Fetch rules when site filter changes
  useEffect(() => {
    if (user && sites.length > 0) {
      fetchRules();
    }
  }, [siteFilter, user, sites.length]);

  // Fetch ledger when site filter or vehicle filter changes (for ledger tab)
  useEffect(() => {
    if (user && activeTab === "ledger" && !ledgerLoading && ledger.length === 0) {
      fetchLedger();
    }
  }, [activeTab, user]);

  // Refetch ledger when filters change
  useEffect(() => {
    if (user && activeTab === "ledger") {
      fetchLedger();
    }
  }, [siteFilter, debouncedVehicleFilter]);

  // Fetch stats when site filter changes
  useEffect(() => {
    if (user) {
      fetchStats();
    }
  }, [siteFilter, user]);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
    } catch (error) {
      navigate("/admin/login");
    }
  };

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const [sitesRes, rulesRes, statsRes] = await Promise.all([
        axios.get(`${API}/sites`, { withCredentials: true }),
        axios.get(`${API}/reward-rules?include_inactive=true`, { withCredentials: true }),
        axios.get(`${API}/credit-ledger/stats`, { withCredentials: true })
      ]);
      setSites(sitesRes.data);
      setRules(rulesRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error("Failed to fetch initial data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRules = async () => {
    setRulesLoading(true);
    try {
      const rulesUrl = siteFilter === "all" 
        ? `${API}/reward-rules?include_inactive=true`
        : `${API}/reward-rules?site_id=${siteFilter}&include_inactive=true`;
      const rulesRes = await axios.get(rulesUrl, { withCredentials: true });
      setRules(rulesRes.data);
    } catch (error) {
      console.error("Failed to fetch rules:", error);
    } finally {
      setRulesLoading(false);
    }
  };

  const fetchLedger = async () => {
    setLedgerLoading(true);
    try {
      let ledgerUrl = `${API}/credit-ledger?limit=100`;
      if (siteFilter !== "all") ledgerUrl += `&site_id=${siteFilter}`;
      if (debouncedVehicleFilter) ledgerUrl += `&vehicle_registration=${debouncedVehicleFilter}`;
      const ledgerRes = await axios.get(ledgerUrl, { withCredentials: true });
      setLedger(ledgerRes.data);
    } catch (error) {
      console.error("Failed to fetch ledger:", error);
    } finally {
      setLedgerLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const statsUrl = siteFilter === "all" 
        ? `${API}/credit-ledger/stats`
        : `${API}/credit-ledger/stats?site_id=${siteFilter}`;
      const statsRes = await axios.get(statsUrl, { withCredentials: true });
      setStats(statsRes.data);
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    }
  };

  const handleSaveRule = async () => {
    if (!ruleForm.name || !ruleForm.site_id || !ruleForm.credit_value) {
      toast.error("Name, site, and credit value are required");
      return;
    }
    
    if (ruleForm.rule_type === "bonus_threshold") {
      if (!ruleForm.visits_required || !ruleForm.time_window_days) {
        toast.error("Visits required and time window are required for bonus rules");
        return;
      }
    }
    
    try {
      const payload = {
        name: ruleForm.name,
        site_id: ruleForm.site_id,
        rule_type: ruleForm.rule_type,
        credit_value: parseFloat(ruleForm.credit_value),
        visits_required: ruleForm.rule_type === "bonus_threshold" ? parseInt(ruleForm.visits_required) : null,
        time_window_days: ruleForm.rule_type === "bonus_threshold" ? parseInt(ruleForm.time_window_days) : null
      };
      
      if (editingRule) {
        await axios.patch(`${API}/reward-rules/${editingRule.rule_id}`, payload, { withCredentials: true });
        toast.success("Reward rule updated");
      } else {
        await axios.post(`${API}/reward-rules`, payload, { withCredentials: true });
        toast.success("Reward rule created");
      }
      setShowRuleDialog(false);
      resetRuleForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save rule");
    }
  };

  const handleToggleRule = async (rule) => {
    try {
      await axios.patch(`${API}/reward-rules/${rule.rule_id}`, 
        { is_active: !rule.is_active }, 
        { withCredentials: true }
      );
      toast.success(rule.is_active ? "Rule deactivated" : "Rule activated");
      fetchData();
    } catch (error) {
      toast.error("Failed to update rule");
    }
  };

  const openEditRule = (rule) => {
    setEditingRule(rule);
    setRuleForm({
      site_id: rule.site_id,
      name: rule.name,
      rule_type: rule.rule_type,
      credit_value: rule.credit_value.toString(),
      visits_required: rule.visits_required?.toString() || "",
      time_window_days: rule.time_window_days?.toString() || ""
    });
    setShowRuleDialog(true);
  };

  const resetRuleForm = () => {
    setEditingRule(null);
    setRuleForm({
      site_id: sites[0]?.site_id || "",
      name: "",
      rule_type: "per_visit",
      credit_value: "",
      visits_required: "",
      time_window_days: ""
    });
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {}
    navigate("/admin/login");
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  return (
    <div className="admin-container min-h-screen" data-testid="admin-rewards">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-full w-64 bg-slate-900 border-r border-slate-800 p-6">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-blue-600 to-green-500 flex items-center justify-center">
            <Car className="w-5 h-5 text-white" />
          </div>
          <span className="text-lg font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
            ParkWise
          </span>
        </div>

        <nav className="space-y-2">
          <Link to="/admin/dashboard" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
            <LayoutDashboard className="w-5 h-5" />
            <span>Dashboard</span>
          </Link>
          <Link to="/admin/registrations" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
            <FileText className="w-5 h-5" />
            <span>Registrations</span>
          </Link>
          <Link to="/admin/sites" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
            <Building className="w-5 h-5" />
            <span>Sites & Kiosks</span>
          </Link>
          <Link to="/admin/retailers" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
            <Store className="w-5 h-5" />
            <span>Retailers</span>
          </Link>
          <Link to="/admin/rewards" className="flex items-center gap-3 px-4 py-3 rounded-lg bg-slate-800 text-white">
            <Gift className="w-5 h-5" />
            <span>Rewards</span>
          </Link>
          <Link to="/admin/analytics" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
            <BarChart3 className="w-5 h-5" />
            <span>Analytics</span>
          </Link>
          <Link to="/admin/ocr-exceptions" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
            <AlertTriangle className="w-5 h-5" />
            <span>OCR Exceptions</span>
          </Link>
          <Link to="/admin/audit-log" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
            <History className="w-5 h-5" />
            <span>Audit Log</span>
          </Link>
          
          {/* Commercial Section */}
          <div className="pt-4 mt-4 border-t border-slate-700">
            <p className="text-xs text-slate-500 uppercase tracking-wider px-4 mb-2">Commercial</p>
            <Link to="/admin/commercial" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
              <Wallet className="w-5 h-5" /><span>Commercial Dashboard</span>
            </Link>
            <Link to="/admin/commercial/finance" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
              <DollarSign className="w-5 h-5" /><span>Finance Centre</span>
            </Link>
            <Link to="/admin/commercial/products-tariffs" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
              <Package className="w-5 h-5" /><span>Products & Tariffs</span>
            </Link>
          </div>
        </nav>

        <div className="absolute bottom-6 left-6 right-6">
          <div className="flex items-center gap-3 mb-4">
            <ParkWiseLogo size="sm" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.name}</p>
              <p className="text-xs text-slate-400 truncate">{user?.email}</p>
            </div>
          </div>
          <button onClick={handleLogout} className="flex items-center gap-2 text-slate-400 hover:text-white text-sm w-full">
            <LogOut className="w-4 h-4" />
            <span>Sign out</span>
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="ml-64 p-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Rewards & Credits
            </h1>
            <p className="text-slate-400">Manage reward rules and view credit ledger</p>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <Card 
              className="admin-card border-slate-800 cursor-pointer hover:border-amber-500/50 transition-colors"
              onClick={() => {
                setActiveTab("ledger");
                setVehicleFilter("");
                fetchLedger();
              }}
              data-testid="kpi-total-credits"
            >
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-400">Total Credits Issued</p>
                    <p className="text-2xl font-bold text-white">{stats.total_credits_issued?.toLocaleString() || 0}</p>
                  </div>
                  <Coins className="w-8 h-8 text-amber-400" />
                </div>
              </CardContent>
            </Card>
            <Card 
              className="admin-card border-slate-800 cursor-pointer hover:border-blue-500/50 transition-colors"
              onClick={() => {
                setActiveTab("ledger");
                setVehicleFilter("");
                fetchLedger();
              }}
              data-testid="kpi-transactions"
            >
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-400">Transactions</p>
                    <p className="text-2xl font-bold text-white">{stats.total_transactions || 0}</p>
                  </div>
                  <BarChart3 className="w-8 h-8 text-blue-400" />
                </div>
              </CardContent>
            </Card>
            <Card 
              className="admin-card border-slate-800 cursor-pointer hover:border-green-500/50 transition-colors"
              onClick={() => {
                setActiveTab("ledger");
                setVehicleFilter("");
                fetchLedger();
              }}
              data-testid="kpi-vehicles-rewarded"
            >
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-400">Vehicles Rewarded</p>
                    <p className="text-2xl font-bold text-white">{stats.unique_vehicles_rewarded || 0}</p>
                  </div>
                  <Car className="w-8 h-8 text-green-400" />
                </div>
              </CardContent>
            </Card>
            <Card 
              className="admin-card border-slate-800 cursor-pointer hover:border-purple-500/50 transition-colors"
              onClick={() => {
                setActiveTab("rules");
                setShowActiveRulesOnly(true);
                fetchRules();
              }}
              data-testid="kpi-active-rules"
            >
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-400">Active Rules</p>
                    <p className="text-2xl font-bold text-white">{rules.filter(r => r.is_active).length}</p>
                  </div>
                  <Gift className="w-8 h-8 text-purple-400" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <div className="flex items-center justify-between">
            <TabsList className="bg-slate-800">
              <TabsTrigger value="rules" className="data-[state=active]:bg-slate-700">
                Reward Rules
              </TabsTrigger>
              <TabsTrigger value="ledger" className="data-[state=active]:bg-slate-700">
                Credit Ledger
              </TabsTrigger>
            </TabsList>
            
            <div className="flex items-center gap-4">
              {/* Site Filter */}
              <Select value={siteFilter} onValueChange={setSiteFilter}>
                <SelectTrigger className="w-[200px] bg-slate-900 border-slate-700 text-white" data-testid="site-filter">
                  <MapPin className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Filter by site" />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700">
                  <SelectItem value="all" className="text-white">All Sites</SelectItem>
                  {sites.map(site => (
                    <SelectItem key={site.site_id} value={site.site_id} className="text-white">
                      {site.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {activeTab === "rules" && (
                <Button 
                  onClick={() => {
                    resetRuleForm();
                    setShowRuleDialog(true);
                  }}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="add-rule-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Rule
                </Button>
              )}
            </div>
          </div>

          {/* Rules Tab */}
          <TabsContent value="rules">
            {/* Active Rules Filter */}
            <div className="mb-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Button
                  variant={showActiveRulesOnly ? "default" : "outline"}
                  size="sm"
                  onClick={() => setShowActiveRulesOnly(!showActiveRulesOnly)}
                  className={showActiveRulesOnly ? "bg-purple-600 hover:bg-purple-700" : ""}
                  data-testid="active-rules-filter"
                >
                  {showActiveRulesOnly ? "Showing Active Only" : "Show All Rules"}
                </Button>
                {rulesLoading && <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />}
              </div>
              <div className="text-sm text-slate-400">
                <span className="mr-4"><strong className="text-white">{rules.filter(r => r.is_active).length}</strong> active</span>
                <span><strong className="text-white">{rules.filter(r => !r.is_active).length}</strong> inactive</span>
              </div>
            </div>
            
            {loading || rulesLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
              </div>
            ) : (showActiveRulesOnly ? rules.filter(r => r.is_active) : rules).length === 0 ? (
              <Card className="admin-card border-slate-800">
                <CardContent className="py-12 text-center">
                  <Gift className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                  <p className="text-slate-400">
                    {showActiveRulesOnly 
                      ? "No active rules found. Toggle the filter to see all rules."
                      : "No reward rules found. Create your first rule to start rewarding visits."}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <Card className="admin-card border-slate-800">
                <Table>
                  <TableHeader>
                    <TableRow className="border-slate-800 hover:bg-slate-800/50">
                      <TableHead className="text-slate-400">Name</TableHead>
                      <TableHead className="text-slate-400">Site</TableHead>
                      <TableHead className="text-slate-400">Type</TableHead>
                      <TableHead className="text-slate-400">Credits</TableHead>
                      <TableHead className="text-slate-400">Threshold</TableHead>
                      <TableHead className="text-slate-400">Status</TableHead>
                      <TableHead className="text-slate-400">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(showActiveRulesOnly ? rules.filter(r => r.is_active) : rules).map(rule => (
                      <TableRow key={rule.rule_id} className="border-slate-800 hover:bg-slate-800/50">
                        <TableCell className="text-white font-medium">{rule.name}</TableCell>
                        <TableCell className="text-slate-300">{rule.site_name}</TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            rule.rule_type === 'per_visit' 
                              ? 'bg-blue-500/20 text-blue-400' 
                              : 'bg-purple-500/20 text-purple-400'
                          }`}>
                            {rule.rule_type === 'per_visit' ? 'Per Visit' : 'Bonus'}
                          </span>
                        </TableCell>
                        <TableCell className="text-amber-400 font-medium">{rule.credit_value}</TableCell>
                        <TableCell className="text-slate-300">
                          {rule.rule_type === 'bonus_threshold' 
                            ? `${rule.visits_required} visits / ${rule.time_window_days} days`
                            : '-'
                          }
                        </TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 rounded text-xs ${
                            rule.is_active 
                              ? 'bg-green-500/20 text-green-400' 
                              : 'bg-slate-500/20 text-slate-400'
                          }`}>
                            {rule.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditRule(rule)}
                              className="text-slate-400 hover:text-white h-8 w-8 p-0"
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleToggleRule(rule)}
                              className={`h-8 px-2 ${
                                rule.is_active 
                                  ? 'text-orange-400 hover:text-orange-300' 
                                  : 'text-green-400 hover:text-green-300'
                              }`}
                            >
                              {rule.is_active ? 'Deactivate' : 'Activate'}
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
            )}
          </TabsContent>

          {/* Ledger Tab */}
          <TabsContent value="ledger">
            {/* Vehicle Filter and Stats Summary */}
            <div className="mb-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Input
                  value={vehicleFilter}
                  onChange={(e) => setVehicleFilter(e.target.value.toUpperCase())}
                  placeholder="Filter by vehicle registration..."
                  className="max-w-xs bg-slate-900 border-slate-700 text-white"
                  data-testid="vehicle-filter-input"
                />
                {ledgerLoading && <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />}
              </div>
              <div className="text-sm text-slate-400">
                <span className="mr-4"><strong className="text-white">{ledger.length}</strong> transactions</span>
                <span><strong className="text-white">{new Set(ledger.map(e => e.vehicle_registration)).size}</strong> unique vehicles</span>
              </div>
            </div>
            
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
              </div>
            ) : ledger.length === 0 ? (
              <Card className="admin-card border-slate-800">
                <CardContent className="py-12 text-center">
                  <Coins className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                  <p className="text-slate-400">No credit transactions found.</p>
                </CardContent>
              </Card>
            ) : (
              <Card className="admin-card border-slate-800">
                <Table>
                  <TableHeader>
                    <TableRow className="border-slate-800 hover:bg-slate-800/50">
                      <TableHead className="text-slate-400">Timestamp</TableHead>
                      <TableHead className="text-slate-400">Vehicle</TableHead>
                      <TableHead className="text-slate-400">Site</TableHead>
                      <TableHead className="text-slate-400">Retailer</TableHead>
                      <TableHead className="text-slate-400">Rule</TableHead>
                      <TableHead className="text-slate-400">Credits</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {ledger.map(entry => (
                      <TableRow key={entry.entry_id} className="border-slate-800 hover:bg-slate-800/50">
                        <TableCell className="text-slate-300 text-sm">{formatDate(entry.timestamp)}</TableCell>
                        <TableCell className="text-white font-mono">{entry.vehicle_registration}</TableCell>
                        <TableCell className="text-slate-300">{entry.site_name}</TableCell>
                        <TableCell className="text-slate-300">{entry.retailer_name}</TableCell>
                        <TableCell className="text-slate-300">{entry.rule_name}</TableCell>
                        <TableCell className="text-amber-400 font-medium">+{entry.credit_value}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* Create/Edit Rule Dialog */}
      <Dialog open={showRuleDialog} onOpenChange={setShowRuleDialog}>
        <DialogContent className="bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white">
              {editingRule ? "Edit Reward Rule" : "Create Reward Rule"}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {editingRule 
                ? "Update the reward rule settings"
                : "Create a new reward rule to incentivize visits"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div>
              <Label className="text-slate-300">Rule Name *</Label>
              <Input
                value={ruleForm.name}
                onChange={(e) => setRuleForm({ ...ruleForm, name: e.target.value })}
                placeholder="e.g. Visit Reward"
                className="bg-slate-800 border-slate-700 text-white mt-2"
              />
            </div>
            <div>
              <Label className="text-slate-300">Site *</Label>
              <Select 
                value={ruleForm.site_id} 
                onValueChange={(v) => setRuleForm({ ...ruleForm, site_id: v })}
                disabled={!!editingRule}
              >
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white mt-2">
                  <SelectValue placeholder="Select site" />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700">
                  {sites.map(site => (
                    <SelectItem key={site.site_id} value={site.site_id} className="text-white">
                      {site.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-300">Rule Type *</Label>
              <Select 
                value={ruleForm.rule_type} 
                onValueChange={(v) => setRuleForm({ ...ruleForm, rule_type: v })}
                disabled={!!editingRule}
              >
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white mt-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700">
                  <SelectItem value="per_visit" className="text-white">Per Visit</SelectItem>
                  <SelectItem value="bonus_threshold" className="text-white">Bonus Threshold</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">
                {ruleForm.rule_type === 'per_visit' 
                  ? 'Award credits for every rewardable visit'
                  : 'Award bonus credits when visit threshold is reached'
                }
              </p>
            </div>
            <div>
              <Label className="text-slate-300">Credit Value *</Label>
              <Input
                type="number"
                value={ruleForm.credit_value}
                onChange={(e) => setRuleForm({ ...ruleForm, credit_value: e.target.value })}
                placeholder="e.g. 10"
                className="bg-slate-800 border-slate-700 text-white mt-2"
              />
            </div>
            {ruleForm.rule_type === 'bonus_threshold' && (
              <>
                <div>
                  <Label className="text-slate-300">Visits Required *</Label>
                  <Input
                    type="number"
                    value={ruleForm.visits_required}
                    onChange={(e) => setRuleForm({ ...ruleForm, visits_required: e.target.value })}
                    placeholder="e.g. 5"
                    className="bg-slate-800 border-slate-700 text-white mt-2"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Time Window (days) *</Label>
                  <Input
                    type="number"
                    value={ruleForm.time_window_days}
                    onChange={(e) => setRuleForm({ ...ruleForm, time_window_days: e.target.value })}
                    placeholder="e.g. 30"
                    className="bg-slate-800 border-slate-700 text-white mt-2"
                  />
                </div>
              </>
            )}
            <div className="flex gap-3 pt-2">
              <Button 
                onClick={() => setShowRuleDialog(false)} 
                variant="outline" 
                className="flex-1 border-slate-600 text-slate-300"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleSaveRule} 
                className="flex-1 bg-blue-600 hover:bg-blue-700"
              >
                {editingRule ? "Save Changes" : "Create Rule"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
